/**
 * Camera state — a pivot + rotation applied to the world container.
 *
 * The renderer positions the world container so that the tile-space
 * pivot `(targetX, targetY)` appears at screen center + trailOffset,
 * rotated by `headingCurrent` (radians, Pixi CW). This gives us:
 *   - "world rotates so the player always faces up on screen"
 *     (heading follows player facing)
 *   - "focus lock centers the enemy, world rotates so enemy is up"
 *     (heading follows enemy → player vector)
 * both from the same math.
 *
 * `targetX/Y` and `headingCurrent` are smoothed values that lerp toward
 * the desired inputs each frame; caller sets the desired via
 * updateCameraSmoothed.
 *
 * `x/y` are legacy screen offsets kept for anything still reading them
 * — the renderer sources everything from pivot+rotation now.
 */

export interface Camera {
  /** Pivot in tile-space. Snapped to player each frame (no lerp) so the
   *  player stays dead-center on screen. */
  targetX: number;
  targetY: number;
  /** World scale factor. 1.0 = default tile size. */
  zoom: number;
  /** Desired rotation this frame (radians, Pixi CW positive). */
  heading: number;
  /** Smoothed rotation — the world container's actual rotation. */
  headingCurrent: number;
  /** Extra screen-Y offset applied to world.position — positive pushes
   *  the pivot BELOW screen center. Kept for opt-in trail behavior. */
  trailOffsetY: number;
  /** Legacy — kept so anything still reading cam.x/y doesn't crash. */
  x: number;
  y: number;
}

export function createCamera(): Camera {
  return {
    targetX: 0,
    targetY: 0,
    zoom: 1,
    heading: 0,
    headingCurrent: 0,
    trailOffsetY: 0,
    x: 0,
    y: 0,
  };
}

/**
 * Snap the pivot to `(targetX, targetY)` with no smoothing. Used for
 * priming the camera on first frame.
 */
export function updateCamera(
  cam: Camera,
  targetX: number,
  targetY: number,
  _tileSize: number,
  _screenW: number,
  _screenH: number,
): void {
  cam.targetX = targetX;
  cam.targetY = targetY;
}

/**
 * Snap the pivot to (desiredX, desiredY) with no smoothing, then lerp
 * the heading toward its target. Position never lags — the entity's
 * visualX/Y already interpolates smoothly via the fixed-tick alpha, so
 * feeding that in directly gives a dead-center camera that follows
 * the player without a rubber-banded feel.
 */
export function updateCameraSmoothed(
  cam: Camera,
  desiredX: number,
  desiredY: number,
  _posRate: number,
  _tileSize: number,
  _screenW: number,
  _screenH: number,
  desiredHeading?: number,
  headingRate = 0.08,
  desiredTrailPx?: number,
  trailRate = 0.5,
): void {
  cam.targetX = desiredX;
  cam.targetY = desiredY;
  if (desiredHeading !== undefined && !Number.isNaN(desiredHeading)) {
    cam.heading = desiredHeading;
    // Shortest-arc lerp: wrap delta into [-π, π] first.
    let dh = cam.heading - cam.headingCurrent;
    while (dh > Math.PI) dh -= 2 * Math.PI;
    while (dh < -Math.PI) dh += 2 * Math.PI;
    cam.headingCurrent += dh * headingRate;
  }
  if (desiredTrailPx !== undefined) {
    cam.trailOffsetY += (desiredTrailPx - cam.trailOffsetY) * trailRate;
  }
}

/** Compute the desired heading so `(facingDx, facingDy)` maps to
 *  screen-north (up). Player-forward-up camera model. */
export function headingForFacing(facingDx: number, facingDy: number): number {
  if (facingDx === 0 && facingDy === 0) return 0;
  const angleV = Math.atan2(facingDy, facingDx);
  return -Math.PI / 2 - angleV;
}

/** Compute the desired heading so the vector `(dx, dy)` (enemy → player)
 *  maps to screen-south (down). Focus-orbit camera model — enemy stays
 *  centered, player circles them at the bottom. */
export function headingForFocus(dx: number, dy: number): number {
  if (dx === 0 && dy === 0) return 0;
  return Math.PI / 2 - Math.atan2(dy, dx);
}
